'use client';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionList, Box, InlineStack, useBreakpoints } from '@shopify/polaris';
import {
  AppsIcon,
  PlusCircleIcon,
  LogoInstagramIcon,
  LogoTiktokIcon,
  MagicIcon,
  StoreIcon,
  FolderUpIcon,
  ImageIcon,
} from '@shopify/polaris-icons';

import {
  ImagesModalRecommendation,
  ImagesModalSectionProps,
  MediaModalSectionName,
} from '@/features/media-modal/components/common/commonTypes';
import { MediaAsset, MediaAssetType } from '@/types/media';
import LibraryModalSection from './modal-sections/LibraryModalSection';
import StockMediaModalSection from './modal-sections/StockMediaModalSection';
import { AiImagesModalSection } from './modal-sections/AiImagesModalSection';
import { AiVideoModalSection } from './modal-sections/AiVideoModalSection';
import { ReplaceBackgroundModalSection } from './modal-sections/ReplaceBackgroundModalSection';
import { ProductImagesModalSection } from './modal-sections/ProductImagesModalSection';
import { UploadMediaAssetModalSection } from './modal-sections/UploadMediaAssetModalSection';
import { IntegrationMediaModalSection } from './modal-sections/IntegrationMediaModalSection';
import { PersonalAvatarAiImageSection } from './modal-sections/PersonalAvatarAiImageSection';
import BunuImagesModalSection from './modal-sections/BunuImagesModalSection';
import BunuVideosModalSection from './modal-sections/BunuVideosModalSection';
import { PolarisModalProvider } from '@/providers/PolarisModalProvider';
import { Product } from '@/types/product';
import { AiTextToVideoModalSection } from './modal-sections/AiTextToVideoModalSection';
import { SessionBanner } from '@/types/banners';
import { useBannerState } from '@/hooks/useBannerState';
const MEDIA_SECTION_ITEMS = [
  {
    title: 'My library',
    items: [
      {
        title: 'Upload file',
        section: MediaModalSectionName.upload,
        icon: FolderUpIcon,
      },
      {
        title: 'My products',
        section: MediaModalSectionName.product,
        icon: StoreIcon,
      },
      {
        title: 'Bunu images',
        section: MediaModalSectionName.bunuImages,
        icon: MagicIcon,
      },
      {
        title: 'Bunu videos',
        section: MediaModalSectionName.bunuVideos,
        icon: MagicIcon,
      },
      {
        title: 'Instagram',
        section: MediaModalSectionName.instagram,
        icon: LogoInstagramIcon,
      },
      {
        title: 'Tiktok',
        section: MediaModalSectionName.tiktok,
        icon: LogoTiktokIcon,
      },
      {
        title: 'Stock assets',
        section: MediaModalSectionName.stock,
        icon: ImageIcon,
      },
    ],
  },
  {
    title: 'Other sources',
    items: [
      {
        title: 'Tolstoy',
        section: MediaModalSectionName.playApp,
        icon: AppsIcon,
      },
    ],
  },
  {
    title: 'AI tools',
    items: [
      {
        title: 'Create AI influencer',
        section: MediaModalSectionName.personalAvatarImage,
        icon: PlusCircleIcon,
        hideIfNotAllowed: true,
      },
      {
        title: 'Create AI video',
        section: MediaModalSectionName.aiVideo,
        icon: PlusCircleIcon,
      },
      {
        title: 'Create AI image',
        section: MediaModalSectionName.aiImage,
        icon: PlusCircleIcon,
      },
      {
        title: 'Create AI background',
        section: MediaModalSectionName.aiBackground,
        icon: PlusCircleIcon,
      },
      // {
      //   title: 'Text to video',
      //   section: MediaModalSectionName.aiTextToVideo,
      //   icon: PlusCircleIcon,
      // },
    ],
  },
];

type Props = {
  product?: Product;
  selectedMedia: MediaAsset[];
  setSelectedMedia: React.Dispatch<React.SetStateAction<MediaAsset[]>>;
  setSelectedImagesAndClose: (images: MediaAsset[]) => void;
  recommendation?: ImagesModalRecommendation;
  initialSection?: MediaModalSectionName;
  mediaAssetType?: MediaAssetType;
  allowedSections?: MediaModalSectionName[];
  isOnlyImages?: boolean;
  multiSelection?: boolean;
};

const SECTIONS: Record<MediaModalSectionName, (props: ImagesModalSectionProps) => any> = {
  stock: StockMediaModalSection,
  aiTextToVideo: AiTextToVideoModalSection,
  aiImage: AiImagesModalSection,
  aiBackground: ReplaceBackgroundModalSection,
  aiVideo: AiVideoModalSection,
  product: ProductImagesModalSection,
  upload: UploadMediaAssetModalSection,
  playApp: IntegrationMediaModalSection,
  instagram: IntegrationMediaModalSection,
  googleDrive: IntegrationMediaModalSection,
  dropbox: IntegrationMediaModalSection,
  tiktok: IntegrationMediaModalSection,
  personalAvatarImage: PersonalAvatarAiImageSection,
  bunuImages: BunuImagesModalSection,
  bunuVideos: BunuVideosModalSection,
} as const;

const MediaModalContent = ({
  product,
  initialSection,
  setSelectedImagesAndClose,
  recommendation,
  selectedMedia,
  setSelectedMedia,
  mediaAssetType,
  allowedSections,
  isOnlyImages,
  multiSelection = true,
}: Props) => {
  const [selectedSection, setSelectedSection] = useState<MediaModalSectionName>(
    initialSection || MediaModalSectionName.stock
  );
  const SectionComponent = SECTIONS[selectedSection];
  const { smDown } = useBreakpoints();
  useEffect(() => {
    if (initialSection) {
      setSelectedSection(initialSection);
    }
  }, [initialSection]);

  const changeSection = useCallback((section: MediaModalSectionName) => {
    setSelectedSection(section);
  }, []);

  const mediaSectionItems = useMemo(() => {
    const onlyAllowedSections = !!allowedSections;
    const allowedSectionsSet = new Set(allowedSections || []);

    return MEDIA_SECTION_ITEMS.reduce(
      (acc, section) => {
        const filteredItems = section.items.filter((item) => {
          const isExplicitlyAllowed = allowedSectionsSet.has(item.section);
          if (onlyAllowedSections) {
            return isExplicitlyAllowed;
          }

          return !item.hideIfNotAllowed;
        });

        if (filteredItems.length > 0) {
          acc.push({ ...section, items: filteredItems });
        }
        return acc;
      },
      [] as typeof MEDIA_SECTION_ITEMS
    );
  }, [allowedSections]);

  const { useAreBannersHidden } = useBannerState();
  const { data, isLoading, isError } = useAreBannersHidden([
    SessionBanner.FinalVideoAvailable,
    SessionBanner.AnimateToolContentModeration,
    SessionBanner.BackGroundGenerationError,
    SessionBanner.TextToImageGenerationError,
    SessionBanner.PersonalAvatarGenerationError,
    SessionBanner.AudioGenerationError,
  ]);

  return (
    <PolarisModalProvider>
      <InlineStack>
        <Box padding="400">
          <ActionList
            sections={mediaSectionItems.map((section) => ({
              title: smDown ? <div /> : section.title, // empty div, prevents action duplication when resizing
              items: section.items.map((item) => ({
                icon: item.icon,
                content: smDown ? '' : item.title,
                onAction: () => changeSection(item.section),
                active: selectedSection === item.section,
              })),
            }))}
          />
        </Box>
        <div className="flex-1 p-4 pl-0">
          <SectionComponent
            section={selectedSection}
            recommendation={recommendation}
            selectedMedia={selectedMedia}
            setSelectedMedia={setSelectedMedia}
            setSelectedMediaAndClose={setSelectedImagesAndClose}
            product={product}
            setSelectedSection={changeSection}
            preferredMediaAssetType={mediaAssetType}
            isOnlyImages={isOnlyImages}
            multiSelection={multiSelection}
          />
        </div>
      </InlineStack>
    </PolarisModalProvider>
  );
};

export default memo(MediaModalContent);
